// eslint-disable-next-line unicorn/prefer-node-protocol
import EventEmitter from 'events';
import {
  Direction, SecurityType, SecurityTypes, Symbol, CurrencyTypes, OrderRecord,
} from '@qep/types/src/index';
import {Decimal} from 'decimal.js';
import _ from 'lodash';
import {
  ConstantSource, ISource, InstrumentSource, SourceKind,
} from './Source';

// should add this to frontend_types symbol, keeping here for PR legibility
interface Symbol2 extends Symbol {
  currencyType?: CurrencyTypes;
}

type ArrayOfFixedLength<T extends any, N extends number> = readonly T[] & {length: N};

const FX_CURRENCIES = new Set(['AUD', 'CAD', 'EUR', 'GBP', 'JPY', 'NZD']);
const unitBidAsk = {snapshot: {snapshot: {bid: {price: 1}, ask: {price: 1}}}};
const bidLens = _.property<unknown, number>('snapshot.snapshot.bid.price');
const askLens = _.property<unknown, number>('snapshot.snapshot.ask.price');
const midLens = (x: unknown) => new Decimal(bidLens(x) || 0).add(askLens(x) || 0).div(2).toNumber();

export class Product extends EventEmitter {
  contractSize?: number;
  contractUnits?: string;
  contractYYYYMM?: string;
  currency?: string | undefined;
  currencyType?: CurrencyTypes;
  decimalPlaces?: number;
  description?: string;
  exchangeSymbol?: string;
  expiry?: string | undefined;
  firstNoticeDate?: Date;
  isTradeble?: boolean;
  name?: string;
  normalizedExchangeSymbol?: string;
  normalizedSymbol?: string;
  normalizedSymbolWithMic?: string;
  rootSymbol?: string;
  secType?: SecurityType;
  segmentMic: string;
  symbol?: string;
  tickSize?: number;
  underlyingRootSymbol?: string;

  private readonly _sources = new Map<SourceKind, ISource>();

  constructor(symbol: Symbol) {
    super();
    this.contractSize = symbol.contractSize;
    this.contractUnits = symbol.contractUnits;
    this.contractYYYYMM = symbol.contractYYYYMM;
    this.currency = symbol.currency;
    this.currencyType = symbol.currency === 'USX' ? CurrencyTypes.cents : CurrencyTypes.dollars; // USX
    this.decimalPlaces = symbol.decimalPlaces;
    this.description = symbol.description;
    this.exchangeSymbol = symbol.exchangeSymbol;
    this.expiry = symbol.expiry;
    this.firstNoticeDate = symbol.firstNoticeDate;
    this.isTradeble = symbol.isTradeble;
    this.name = symbol.name;
    this.normalizedExchangeSymbol = symbol.normalizedExchangeSymbol;
    this.normalizedSymbol = symbol.normalizedSymbol;
    this.normalizedSymbolWithMic = symbol.normalizedSymbolWithMic;
    this.rootSymbol = symbol.rootSymbol;
    this.secType = symbol.secType;
    this.segmentMic = symbol.segmentMic;
    this.symbol = symbol.symbol;
    this.tickSize = symbol.tickSize;
    this.underlyingRootSymbol = symbol.underlyingRootSymbol ?? undefined;

    this._sources.set(SourceKind.Data, new InstrumentSource(this.symbolDetails));
    const fxSource = FX_CURRENCIES.has(symbol.currency!)
      ? new InstrumentSource({segmentMic: 'XTUP', symbol: `${symbol.currency}-USD`, exchangeSymbol: `${symbol.currency}/USD`})
      : new ConstantSource(unitBidAsk);
    this._sources.set(SourceKind.Fx, fxSource);
  }

  async withSources<N extends number, T>(
    kinds: ArrayOfFixedLength<SourceKind, N> | [never],
    callback: (data: ArrayOfFixedLength<any, N> | [never]) => T,
  ): Promise<T> {
    let timeoutInterval: NodeJS.Timeout;

    const promise = new Promise<T>((resolve, _reject) => {
      const sources = kinds.map(kind => this._sources.get(kind));

      timeoutInterval = setInterval(() => {
        if (sources.some(src => !src?.ready)) {
          return;
        }

        // We can ensure src.value is defined since we have checked all sources are defined and ready
        // and we can ensure that our map is the same number of elements as kinds since we have only mapped
        resolve(callback(sources.map(src => src!.value) as unknown as ArrayOfFixedLength<any, N>));
      }, 9);
    });

    promise.finally(() => {
      clearInterval(timeoutInterval);
    });

    return promise;
  }

  withSourcesSync<N extends number, T>(
    kinds: ArrayOfFixedLength<SourceKind, N> | [never],
    callback: (data: ArrayOfFixedLength<any, N> | [never]) => T,
  ): T | undefined {
    const sources = kinds.map(kind => this._sources.get(kind));
    if (sources.some(src => !src?.ready)) {
      return undefined;
    }

    // We can ensure src.value is defined since we have checked all sources are defined and ready
    // and we can ensure that our map is the same number of elements as kinds since we have only mapped
    return callback(sources.map(src => src!.value) as unknown as ArrayOfFixedLength<any, N>);
  }

  async currencyConversionRate(): Promise<number> {
    return this.withSources([SourceKind.Fx], ([fx]) => midLens(fx));
  }

  async convertCurrency(value: number | Decimal) {
    const centsMultiplier = this.currencyType === CurrencyTypes.cents ? 1 / 100 : 1;
    const currencyConversionRate = await this.currencyConversionRate();

    return new Decimal(value)
      .mul(currencyConversionRate)
      .mul(centsMultiplier)
      .toNumber();
  }

  async calculateNotionalValue(direction: Direction = Direction.Buy, quantity = 1, overridePrice?: number): Promise<number> {
    const price = overridePrice ?? this.getPrice();
    const directionMultiplier = direction === Direction.Buy ? 1 : -1;
    if (price === undefined) {
      return price;
    }

    const notional = new Decimal(this.contractSize ?? 0)
      .mul(quantity)
      .mul(price)
      .mul(directionMultiplier);

    return this.convertCurrency(notional);
  }

  subscribe() {
    for (const [kind, source] of this._sources.entries()) {
      const handler = kind === SourceKind.Data
        ? data => this.emit('data', data)
        : undefined;

      source.subscribe(handler);
    }
  }

  unsubscribe = () => {
    for (const source of this._sources.values()) {
      source?.unsubscribe?.();
    }
  };

  getPrice() {
    return this.withSourcesSync([SourceKind.Data], ([data]) => {
      if (data.snapshot?.snapshot?.ask?.price && data.snapshot?.snapshot?.bid?.price) {
        return this.getMidPrice();
      }

      if (data.snapshot?.snapshot?.lastTrade?.price && data.snapshot?.snapshot?.lastTrade?.price !== '-') {
        return data.snapshot?.snapshot?.lastTrade?.price;
      }

      return data.snapshot?.snapshot?.previousDailySettlement?.settlementPrice;
    });
  }

  getMidPrice() {
    return this.withSourcesSync([SourceKind.Data], ([data]) => {
      try {
        if (data) {
          return midLens(data);
        }

        /**
           * Based on the information here: https://app.datadoghq.com/rum/performance-monitoring?query=%40session.type%3Auser%20%40application.id%3A3d9167db-903e-4843-adc5-d83a7746623f&filters=%5B%22env%22%2C%22service%22%2C%22version%22%2C%22%40geo.country%22%2C%22%40device.type%22%2C%22%40usr.email%22%5D&fromUser=false&tab=errors&from_ts=1730923232594&to_ts=1731096032594&live=true
           * The assumption is that this is called, which causes an execution of 'midPrice', but
           * the data is undefined which ends up returning an error from the Decimal class.
           */
        console.error('No data found for mid price');
        return undefined;
      } catch (error) {
        console.error('Error in mid price calculation', error, data);
        return undefined;
      }
    });
  }

  async getBuyPNL(executedPrice, quantity) {
    const currentPrice: number = this.getPrice();
    const buyPriceDiff = currentPrice ? currentPrice - executedPrice : 0;

    const buyPL = new Decimal(this.contractSize ?? 0)
      .mul(buyPriceDiff)
      .mul(quantity);

    return this.convertCurrency(buyPL);
  }

  async getSellPNL(executedPrice, quantity) {
    const currentPrice: number = this.getPrice();
    const sellPriceDiff = currentPrice ? executedPrice - currentPrice : 0;

    const sellPL = new Decimal(this.contractSize ?? 0)
      .mul(sellPriceDiff)
      .mul(Math.abs(quantity));

    return this.convertCurrency(sellPL);
  }

  getPriceForExpandedTicksCalculation(): number | undefined {
    return this.withSourcesSync([SourceKind.Data], ([data]) => {
      if (data.snapshot?.snapshot?.lastTrade?.price && data.snapshot?.snapshot?.lastTrade?.price !== '-') {
        return data.snapshot?.snapshot?.lastTrade?.price;
      }

      if (data.snapshot?.snapshot?.bid?.price && (Number(data.snapshot?.snapshot?.bid?.price) % Number(this.tickSize) !== 0)) {
        return data.snapshot?.snapshot?.bid?.price;
      }

      if (data.snapshot?.snapshot?.ask?.price && (Number(data.snapshot?.snapshot?.ask?.price) % Number(this.tickSize) !== 0)) {
        return data.snapshot?.snapshot?.ask?.price;
      }

      if (data.snapshot?.snapshot?.previousDailySettlement?.settlementPrice) {
        return data.snapshot?.snapshot?.previousDailySettlement?.settlementPrice;
      }
    });
  }

  /** This fall-through is intentionally obtuse because the exact starting point
   * for the Orderbook price ladder isn't very consequential as long as it is
   * relatively near the market prices.
   * */
  getStartingLadderPrice(): number {
    return this.withSourcesSync([SourceKind.Data], ([data]) => {
      const {ask, bid, lastTrade, previousDailySettlement} = data.snapshot?.snapshot;
      return lastTrade?.price
        ?? bid?.price
        ?? ask?.price
        ?? previousDailySettlement?.settlementPrice
        ?? 0;
    });
  }

  /**
   *
   * @param totalTicks The total number of additional ticks. This number will be halved, and a tick for each side of the midline will be generated
   *                    (E.g. 200 will return 100 ticks above and 100 ticks below the midline, in addition to the midline)
   * @returns An array of objects containing the price and a string representing the price with the correct number of display decimal places
   */
  getExpandedTicks(totalTicks = 200): Array<{price: number; key: string}> {
    const startingPrice = this.getStartingLadderPrice();
    const startingPriceString = startingPrice?.toFixed(this.decimalPlaces ?? 2);

    // Note: There is a rare edgecase here where the starting price is not
    // evenly divisible by the tickSize. It is intentionally not being handled
    // yet so that we build out the price ladder regardless of market fluidity (QEP-4392).
    if (!(new Decimal(startingPrice).mod(this.tickSize ?? 0).eq(0))) {
      return [{price: Number(startingPrice), key: startingPriceString}];
    }

    const ticks: Array<{price: number; key: string}> = [{price: startingPrice, key: startingPriceString}];
    for (let i = 0; i < Math.floor(totalTicks / 2); i++) {
      const priceUp = Number(startingPrice + (Number(this.tickSize) * i)).toFixed(this.decimalPlaces ?? 2);
      const priceDown = Number(startingPrice - (Number(this.tickSize) * i)).toFixed(this.decimalPlaces ?? 2);
      ticks.unshift({price: Number(priceUp), key: priceUp});
      ticks.push({price: Number(priceDown), key: priceDown});
    }

    return ticks;
  }

  get symbolDetails(): Symbol2 | undefined {
    if (!this.symbol || !this.segmentMic) {
      return undefined;
    }

    return {
      symbol: this.symbol,
      rootSymbol: this.rootSymbol,
      contractSize: this.contractSize,
      contractUnits: this.contractUnits,
      description: this.description,
      normalizedSymbol: this.normalizedSymbol,
      normalizedExchangeSymbol: this.normalizedExchangeSymbol,
      normalizedSymbolWithMic: this.normalizedSymbolWithMic,
      segmentMic: this.segmentMic,
      exchangeSymbol: this.exchangeSymbol,
      secType: this.secType,
      tickSize: this.tickSize,
      currency: this.currency,
      expiry: this.expiry,
      contractYYYYMM: this.contractYYYYMM,
      name: this.name,
      firstNoticeDate: this.firstNoticeDate,
      decimalPlaces: this.decimalPlaces,
      isTradeble: this.isTradeble,
      currencyType: this.currencyType,
      underlyingRootSymbol: this.underlyingRootSymbol,
    };
  }
}

type ExchangeInfo = {
  exchange: string;
  exchangeRate: number;
};

export class FuturesProduct extends Product {
  exchange: string;
  exchangeRate: number;

  constructor(symbol: Symbol, exchangeData: ExchangeInfo) {
    super(symbol);
    this.exchange = exchangeData.exchange;
    this.exchangeRate = exchangeData.exchangeRate;
    this.secType = SecurityTypes.Future;
  }
}

export class FuturesSpreadProduct extends Product {
  legs: OrderRecord[];
  defaultDirection?: Direction;

  constructor(symbol: Symbol, legs: OrderRecord[]) {
    super(symbol);
    this.legs = legs;
    this.defaultDirection = (symbol as Symbol & {defaultDirection?: Direction}).defaultDirection;
  }
}
